/**
 * TaahhutnameBelgesi.jsx - the GENERATED taahhütname as a printable page (backlog #217, built on #216).
 *
 * Nothing is stored as a PDF: the backend returns the commitment as data (GET /projects/{id}/taahhutnameler) and this
 * page is drawn from it every time - the stand / company / fair header ("Yukarıda bilgileri verilen stand ..."), the
 * wording (HTML from the legacy editor, cleaned by GuvenliHtml before it reaches the screen) and the chosen source file.
 * The print button prints this block only (body class `taahhutname-yazdir`, see assets/scss/components/_print.scss).
 * The company side (#218) can reuse it as it is.
 * Next to the print button: "PDF indir: Türkçe | English" (#666, TaahhutnamePdfIndir) - the same document as a generated PDF with the chosen
 * source image and blank signature blocks.
 */
import React from "react";
import { Alert, Button } from "reactstrap";
import { Printer, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import GuvenliHtml from "../Common/GuvenliHtml";
import { fmtTarih } from "./dosyaTakipYardimci";
import TaahhutnamePdfIndir from "./TaahhutnamePdfIndir";

export default function TaahhutnameBelgesi({ header, taahhut, onPreview, projectId }) {
  const { t } = useTranslation("projeler");
  const text = taahhut?.text ?? null;
  const kaynak = taahhut?.source ?? null;
  const h = header ?? {};

  const tarih = h.fuarBaslangic
    ? `${fmtTarih(h.fuarBaslangic)}${h.fuarBitis ? ` - ${fmtTarih(h.fuarBitis)}` : ""}`
    : null;
  const satirlar = [
    ["fuar", h.fuarAdi],
    ["tarih", tarih],
    ["fuarAlani", h.fuarAlaniAdi],
    ["salon", h.salonAdi],
    ["stand", h.standAdi],
    ["katilimci", h.katilimciFirmaAdi],
    ["dekorator", h.dekoratorFirmaAdi],
    ["yukseklik", h.projeYuksekligi != null ? `${h.projeYuksekligi} m` : null],
    ["alan", h.alanM2 != null ? `${h.alanM2} m²` : null],
  ].filter(([, v]) => v != null && v !== "");

  function yazdir() {
    const kapat = () => {
      document.body.classList.remove("taahhutname-yazdir");
      window.removeEventListener("afterprint", kapat);
    };
    document.body.classList.add("taahhutname-yazdir");
    window.addEventListener("afterprint", kapat);
    window.print();
  }

  return (
    <div>
      <div className="d-flex justify-content-end align-items-start flex-wrap gap-2 mb-2">
        <TaahhutnamePdfIndir projectId={projectId} taahhut={taahhut} />
        <Button size="sm" color="primary" outline onClick={yazdir} data-testid="belge-yazdir">
          <Printer size={14} className="me-1" />{t("taahhutnameDurumu.belge.yazdir")}
        </Button>
      </div>

      <div className="taahhutname-belgesi" data-testid="taahhutname-belgesi">
        <h5 className="text-center mb-3">{text?.baslik || taahhut?.label}</h5>

        {satirlar.length > 0 && (
          <div className="mb-3">
            <div className="fw-semibold mb-1">{t("taahhutnameDurumu.belge.standBilgisi")}</div>
            <table className="table table-sm table-borderless w-auto mb-0">
              <tbody>
                {satirlar.map(([k, v]) => (
                  <tr key={k}>
                    <td className="text-muted pe-3">{t(`taahhutnameDurumu.belge.${k}`)}</td>
                    <td className="fw-medium">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {text ? (
          <GuvenliHtml html={text.icerik} />
        ) : (
          <Alert color="warning">{t("taahhutnameDurumu.belge.metinYok")}</Alert>
        )}

        {kaynak && (
          <div className="mt-3 small">
            <span className="text-muted">{t("taahhutnameDurumu.belge.kaynakBelge")}{" "}</span>
            {onPreview ? (
              <Button color="link" size="sm" className="p-0 align-baseline" onClick={() => onPreview({ originalFileName: kaynak.originalFileName, storedFileName: kaynak.storedFileName })}>
                <ExternalLink size={12} className="me-1" />{kaynak.sourceName ?? kaynak.originalFileName}
              </Button>
            ) : (
              <span className="fw-medium">{kaynak.sourceName ?? kaynak.originalFileName}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
